import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import type {
  FreeTrialAnswers,
  FreeTrialStepId,
  SignUpIdentity,
  SkipFlags,
  UseCaseAnswer,
} from 'components/FreeTrialFTUX/config/types';

const STEP_ORDER: FreeTrialStepId[] = ['signUp', 'useCase', 'integration', 'video'];

type QuizStepId = Exclude<FreeTrialStepId, 'signUp'>;

const QUIZ_STEP_NUMBER: Record<QuizStepId, number> = {
  useCase: 1,
  integration: 2,
  video: 3,
};

interface FreeTrialStore {
  currentStep: FreeTrialStepId;
  identity?: SignUpIdentity;
  answers: FreeTrialAnswers;
  skips: SkipFlags;
  highestQuizStepReached: number;
  completed: boolean;

  setIdentity: (identity: SignUpIdentity) => void;
  setUseCase: (answer: UseCaseAnswer) => void;
  setIntegration: (value: string) => void;
  setWatchedVideo: (watched: boolean) => void;
  markSkipped: (step: QuizStepId) => void;
  goToStep: (step: FreeTrialStepId) => void;
  goToNext: () => void;
  markCompleted: () => void;
  reset: () => void;
}

const initialState = {
  currentStep: 'signUp' as FreeTrialStepId,
  identity: undefined,
  answers: {} as FreeTrialAnswers,
  skips: { useCase: false, integration: false, video: false } as SkipFlags,
  highestQuizStepReached: 0,
  completed: false,
};

/**
 * Persists the user's progress in localStorage so an accidental refresh
 * mid-quiz keeps them where they were with their data intact. The
 * useFreeTrialState hook resets the store on mount when completed is
 * true, so users who already finished the flow once always come back
 * to the sign-up form, never to a stale mid-quiz step.
 */
export const useFreeTrialStore = create<FreeTrialStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      setIdentity: identity => set({ identity }),

      setUseCase: useCase =>
        set(state => ({
          answers: { ...state.answers, useCase },
          skips: { ...state.skips, useCase: false },
        })),

      setIntegration: integration =>
        set(state => ({
          answers: { ...state.answers, integration },
          skips: { ...state.skips, integration: false },
        })),

      setWatchedVideo: watched =>
        set(state => ({
          answers: { ...state.answers, watchedVideo: watched },
        })),

      markSkipped: step =>
        set(state => ({
          skips: { ...state.skips, [step]: true },
        })),

      goToStep: step => {
        const stepNumber = step === 'signUp' ? 0 : QUIZ_STEP_NUMBER[step];
        set(state => ({
          currentStep: step,
          highestQuizStepReached: Math.max(state.highestQuizStepReached, stepNumber),
        }));
      },

      goToNext: () => {
        const { currentStep } = get();
        const idx = STEP_ORDER.indexOf(currentStep);
        const next = STEP_ORDER[idx + 1];
        if (!next) {
          return;
        }
        const stepNumber = next === 'signUp' ? 0 : QUIZ_STEP_NUMBER[next];
        set(state => ({
          currentStep: next,
          highestQuizStepReached: Math.max(state.highestQuizStepReached, stepNumber),
        }));
      },

      markCompleted: () => set({ completed: true }),

      reset: () => set(initialState),
    }),
    {
      name: 'free-trial-ftux-storage',
      storage: createJSONStorage(() => localStorage),
      // v2 dropped the invites step; v3 dropped the in-page confirmation step
      // (the flow now redirects to a standalone thank-you page). Bumping the
      // version discards any persisted state that still points at a removed
      // step, which would otherwise render a blank screen.
      version: 3,
      partialize: state => ({
        currentStep: state.currentStep,
        identity: state.identity,
        answers: state.answers,
        skips: state.skips,
        highestQuizStepReached: state.highestQuizStepReached,
        completed: state.completed,
      }),
    },
  ),
);

export const QUIZ_STEPS: QuizStepId[] = ['useCase', 'integration', 'video'];

export const getQuizStepNumber = (step: QuizStepId) => QUIZ_STEP_NUMBER[step];

export const TOTAL_QUIZ_STEPS = QUIZ_STEPS.length;
